import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';

import { preloadPhoto, cleanUrl } from '../../../utils/common';
import { getAuthClient } from '../../../utils/auth';
import { buildYourTourApiUrl } from '../../../utils/ApiHelpers';
import useConfigStore from '../../../stores/configStore';

import ConditionalWrapperDivs from '../../HelperComponents/ConditionalWrapperDivs';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';

import FadingContainer from '../../FadingContainer/FadingContainer';
import PhotoGalleryImage from '../PhotoGalleryImage/PhotoGalleryImage';

import './PhotoGalleryDetail.scss';

const auth = getAuthClient();

const PHOTO_GALLERY_INSTRUCTIONS = 'Use the arrows to browse photos.';

const PhotoGalleryDetail = ({
  id, className, uuid, images, galleryClass, imageClass, isLoading, showInstructions,
}) => {
  // All hooks must be called unconditionally (React Rules of Hooks)
  const [photoGalleryData, setPhotoGalleryData] = useState(null);
  const [firstPicIsLoaded, setFirstPicIsLoaded] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [showControls, setShowControls] = useState(false);
  const [isHomescreenSlideshow, setIsHomescreenSlideshow] = useState(null);
  const [photos, setPhotos] = useState(null);
  const [formattedPhotos, setFormattedPhotos] = useState([]);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const mountedRef = useRef(true);
  const featureFlags = useConfigStore((state) => state.featureFlags);
  const { platformShowHomescreenGalleryControls } = featureFlags || {};

  useEffect(() => {
    setIsHomescreenSlideshow(galleryClass === 'homescreenSlideshow');
  }, [galleryClass]);

  // Show controls during loading or when we have multiple photos. The homescreen
  // slideshow only gets controls if the community has opted in.
  useEffect(() => {
    const isLoadingPhotos = photos === null || isHomescreenSlideshow === null;
    const allowsControls = !isHomescreenSlideshow || platformShowHomescreenGalleryControls;
    const hasMultiplePhotos = photos?.length > 1 && allowsControls;
    setShowControls(isLoadingPhotos || hasMultiplePhotos);
  }, [photos, isHomescreenSlideshow, platformShowHomescreenGalleryControls]);

  useEffect(() => {
    let isMounted = true;
    if (uuid && isHomescreenSlideshow !== null) {
      const fetchData = async () => {
        // Use full_screen style for homescreen slideshow, modal for everything else
        const imageStyle = isHomescreenSlideshow ? 'full_screen' : 'modal';
        const url = buildYourTourApiUrl(`/your_tour_api/photo_gallery/${uuid}`, { image_style: imageStyle });
        const data = await auth.fetchWithAuthentication(url, 'GET', null);
        if (isMounted && data?.images) {
          setPhotoGalleryData(data);
        }
      };
      fetchData();
    }
    return () => {
      isMounted = false;
    };
  }, [uuid, isHomescreenSlideshow]);

  useEffect(() => {
    if (photoGalleryData?.images) {
      setPhotos(photoGalleryData.images);
    }
  }, [photoGalleryData]);

  useEffect(() => {
    if (images) {
      setPhotos(images);
    }
  }, [images]);

  // Format photos for display (clean URLs)
  useEffect(() => {
    if (photos?.length > 0) {
      const rawFormattedPhotos = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const photo of photos) {
        // New API returns image as string, old format has image.modal/fullScreen
        const image = typeof photo.image === 'string'
          ? photo.image
          : (isHomescreenSlideshow ? photo.image.fullScreen : photo.image.modal);
        const formattedPhoto = {
          uuid: photo.id || photo.uuid,
          caption: photo.caption,
          formattedImage: cleanUrl(image),
        };
        rawFormattedPhotos.push(formattedPhoto);
      }
      setFormattedPhotos(rawFormattedPhotos);
    }
  }, [photos, isHomescreenSlideshow]);

  // Preload the first gallery photo and set as background image
  useEffect(() => {
    const handlePreloadingPhotos = async (firstImage) => {
      await preloadPhoto(firstImage);
      if (!mountedRef.current) return;
      setFirstPicIsLoaded(true);
    };
    if (formattedPhotos?.length > 0) {
      const firstImage = formattedPhotos[0].formattedImage;
      setBackgroundImage(firstImage);
      handlePreloadingPhotos(firstImage);
    }
  }, [formattedPhotos]);

  // Preload upcoming slideshow images in background for smooth transitions
  useEffect(() => {
    if (!isHomescreenSlideshow || !firstPicIsLoaded || formattedPhotos.length <= 1) return;

    // Preload next few images after first image is loaded
    const preloadUpcoming = async () => {
      // Preload images 2-4 (indices 1-3) in background
      const imagesToPreload = formattedPhotos.slice(1, 4);
      await Promise.all(imagesToPreload.map((photo) => preloadPhoto(photo.formattedImage)));
    };

    // Use requestIdleCallback to avoid blocking UI
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(preloadUpcoming, { timeout: 3000 });
    } else {
      setTimeout(preloadUpcoming, 100);
    }
  }, [isHomescreenSlideshow, firstPicIsLoaded, formattedPhotos]);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    setActivePhotoIndex(0);
  }, [formattedPhotos]);

  // Automatic slideshow for homescreen — disabled when manual controls are shown.
  useEffect(() => {
    if (
      isHomescreenSlideshow
      && !platformShowHomescreenGalleryControls
      && formattedPhotos.length > 0
    ) {
      const myInterval = setInterval(() => {
        setActivePhotoIndex((prevIndex) => (prevIndex + 1) % formattedPhotos.length);
      }, 6000);
      return () => {
        clearInterval(myInterval);
      };
    }
  }, [isHomescreenSlideshow, platformShowHomescreenGalleryControls, formattedPhotos.length]);

  const handleRightClick = () => {
    setActivePhotoIndex((prevIndex) => (
      prevIndex === formattedPhotos.length - 1 ? 0 : prevIndex + 1
    ));
  };

  const handleLeftClick = () => {
    setActivePhotoIndex((prevIndex) => (
      prevIndex === 0 ? formattedPhotos.length - 1 : prevIndex - 1
    ));
  };

  // Return null if we have no way to get images and we're not in a loading state
  if ((!images || images.length === 0) && !uuid && !isLoading) {
    return null;
  }

  return (
    <FadingContainer>
      <div
        id={id || ''}
        className={className || ''}
      >

        <div className={galleryClass || ''}>
          {showControls && (
            <>
              <button
                type="button"
                className="slideshowControls slideshowControls-left"
                onClick={handleLeftClick}
                disabled={photos === null}
              >
                <span className="arrow">
                  <span />
                  <span />
                </span>
                <span className="sr-only">Previous</span>
              </button>

              <button
                type="button"
                className="slideshowControls slideshowControls-right"
                onClick={handleRightClick}
                disabled={photos === null}
              >
                <span className="arrow">
                  <span />
                  <span />
                </span>
                <span className="sr-only">Next</span>
              </button>
            </>
          )}

          <ConditionalWrapperDivs
            condition={!isHomescreenSlideshow}
            classNamesArray={['slideshowContainer']}
          >
            {!isHomescreenSlideshow && (
            <div
              className="slideshowBackground"
              style={{ backgroundImage: `url(${backgroundImage})` }}
            />
            )}

            <ConditionalWrapperDivs condition={!isHomescreenSlideshow} classNamesArray={[]}>

              {!firstPicIsLoaded && <LoadingSpinner />}

              {formattedPhotos?.length > 0
              && formattedPhotos.map((photo, i) => (
                <PhotoGalleryImage
                  key={photo.uuid}
                  uuid={photo.uuid || photo.id}
                  image={photo.formattedImage}
                  caption={photo.caption}
                  className={`${imageClass} ${i === activePhotoIndex && firstPicIsLoaded ? 'slideshowPhoto--active' : ''
                  }`}
                  count={formattedPhotos.length}
                  index={i}
                />
              ))}
            </ConditionalWrapperDivs>
          </ConditionalWrapperDivs>

        </div>
        {showInstructions && formattedPhotos?.length > 1 && (
          <div className="detail__footnote">
            {PHOTO_GALLERY_INSTRUCTIONS}
          </div>
        )}
      </div>

    </FadingContainer>
  );
};

PhotoGalleryDetail.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  uuid: PropTypes.string,
  showInstructions: PropTypes.bool,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      uuid: PropTypes.string,
      caption: PropTypes.string,
      // Supports both new API format (string) and legacy format (object)
      image: PropTypes.oneOfType([PropTypes.string,
        PropTypes.shape({
          fullScreen: PropTypes.string,
          modal: PropTypes.string,
        })]),
    }),
  ),
  galleryClass: PropTypes.string,
  imageClass: PropTypes.string,
  isLoading: PropTypes.bool,
};

PhotoGalleryDetail.defaultProps = {
  id: null,
  className: null,
  uuid: null,
  showInstructions: false,
  images: null,
  galleryClass: null,
  imageClass: null,
  isLoading: false,
};

export default PhotoGalleryDetail;
